import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppApproach } from '@/contexts/AppModuleContext';
import {
  Search,
  Calendar,
  Download,
  MoreHorizontal,
  Filter,
  SortDesc,
  SortAsc,
  Clock,
  MessageCircle,
  Mic,
  Video,
  ChevronDown,
  Trash2,
  FileText,
  CheckCircle2
} from 'lucide-react';

type SessionType = 'text' | 'voice' | 'video';
type SortOption = 'date' | 'duration' | 'title' | 'type';
type FilterOption = 'all' | 'text' | 'voice' | 'video';

interface SessionInsight {
  id: string;
  text: string;
  category: 'breakthrough' | 'practice' | 'goal' | 'reflection';
}

interface Session {
  id: string;
  title: string;
  date: string;
  duration: string;
  durationMinutes: number;
  type: SessionType;
  insights: SessionInsight[];
  summary: string;
  transcript?: string;
}

export function AppPastSessions() {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');

  // Get current coaching approach
  const { currentApproach, approachConfig } = useAppApproach();

  // Enhanced session data based on coaching approach
  const pastSessions: Session[] = currentApproach === 'agile' ? [
    {
      id: 'session-1',
      title: 'From Command-Control to Partnership Leadership',
      date: 'May 15, 2025',
      duration: '48 min',
      durationMinutes: 48,
      type: 'voice',
      insights: [
        { id: 'i1', text: 'Realized I was seeking approval for decisions I already had authority to make', category: 'breakthrough' },
        { id: 'i2', text: 'Shifted from "telling teams what to do" to "coaching teams to find solutions"', category: 'practice' },
        { id: 'i3', text: 'Team now self-organizes around complex problems - velocity up 30%', category: 'goal' },
      ],
      summary: 'Identity-shifting session on servant leadership. Discovered how approval-seeking was blocking team autonomy and learned to truly make people awesome.',
    },
    {
      id: 'session-2',
      title: 'Advanced Ceremony Facilitation - Transforming Team Dynamics',
      date: 'May 10, 2025',
      duration: '52 min',
      durationMinutes: 52,
      type: 'video',
      insights: [
        { id: 'i4', text: 'Discovered retrospectives were reinforcing blame culture instead of learning', category: 'breakthrough' },
        { id: 'i5', text: 'Designed "failure celebration" format that transformed team psychology', category: 'practice' },
        { id: 'i6', text: 'Team energy scores jumped from 3/10 to 8/10 after ceremony redesign', category: 'goal' },
      ],
      summary: 'Transformational work on ceremony design as culture change. Retrospectives now drive real behavioral change and team engagement increased 60%.',
    },
    {
      id: 'session-3',
      title: 'Breakthrough Coaching Conversations - Unlocking Team Potential',
      date: 'May 3, 2025',
      duration: '41 min',
      durationMinutes: 41,
      type: 'text',
      insights: [
        { id: 'i7', text: 'Realized I was solving problems for the team instead of building their capability', category: 'breakthrough' },
        { id: 'i8', text: 'Practiced coaching stance through team member\'s performance challenge', category: 'practice' },
        { id: 'i9', text: 'Three team members now proactively seeking coaching conversations', category: 'goal' },
      ],
      summary: 'Profound shift from directive management to coaching leadership. Team member ownership and problem-solving capability increased dramatically.',
    },
    {
      id: 'session-4',
      title: 'Building Anti-Fragile Self-Organizing Teams',
      date: 'April 28, 2025',
      duration: '44 min',
      durationMinutes: 44,
      type: 'voice',
      insights: [
        { id: 'i10', text: 'Team was addicted to my problem-solving and avoiding difficult decisions', category: 'breakthrough' },
        { id: 'i11', text: 'Created "no rescue" experiment - team must solve own impediments first', category: 'practice' },
        { id: 'i12', text: 'Team velocity increased 25% and escalations decreased 70%', category: 'goal' },
      ],
      summary: 'Powerful work on organizational co-dependency. Team now thrives under pressure and consistently self-organizes around complex challenges.',
    },
    {
      id: 'session-5',
      title: 'Psychological Safety as Competitive Advantage',
      date: 'April 20, 2025',
      duration: '46 min',
      durationMinutes: 46,
      type: 'video',
      insights: [
        { id: 'i13', text: 'Connected team silence to organizational fear culture and performance impact', category: 'breakthrough' },
        { id: 'i14', text: 'Modeled vulnerability by sharing my own leadership failures publicly', category: 'practice' },
        { id: 'i15', text: 'Team now proactively surfaces problems 3x faster than before', category: 'goal' },
      ],
      summary: 'Breakthrough work connecting psychological safety to business outcomes. Team innovation and risk-taking transformed organizational delivery capability.',
    },
    {
      id: 'session-6',
      title: 'Scaling Agile Leadership Across the Organization',
      date: 'April 15, 2025',
      duration: '38 min',
      durationMinutes: 38,
      type: 'text',
      insights: [
        { id: 'i16', text: 'Recognized need to develop other servant leaders to scale impact', category: 'breakthrough' },
        { id: 'i17', text: 'Created peer coaching program for other scrum masters', category: 'practice' },
        { id: 'i18', text: 'Four other teams now requesting servant leadership coaching', category: 'goal' },
      ],
      summary: 'Strategic session on organizational transformation. Developing distributed leadership capability that multiplies agile culture impact across multiple teams.',
    },
    {
      id: 'session-7',
      title: 'Navigating Stakeholder Prioritization Paralysis',
      date: 'June 12, 2025',
      duration: '25 min',
      durationMinutes: 25,
      type: 'video',
      insights: [
        { id: 'i19', text: 'Haven\'t even reached the point of prioritization - completely paralyzed by competing demands', category: 'breakthrough' },
        { id: 'i20', text: 'Setting up collaborative prioritization meetings to involve stakeholders in decisions', category: 'practice' },
        { id: 'i21', text: 'Will implement MoSCoW method (Must/Should/Could/Won\'t have) in stakeholder sessions', category: 'goal' },
      ],
      summary: 'Powerful session addressing prioritization paralysis with marketing, sales, and client success stakeholders. Shifted from solo decision-making struggle to collaborative MoSCoW framework approach.',
    },
  ] : [
    // Original ontological coaching sessions
    {
      id: 'session-1',
      title: 'Staying Centered When Things Get Tense',
      date: 'May 15, 2025',
      duration: '32 min',
      durationMinutes: 32,
      type: 'voice',
      insights: [
        { id: 'i1', text: 'Identified trigger points during team feedback sessions', category: 'breakthrough' },
        { id: 'i2', text: 'Practiced breathing techniques for emotional regulation', category: 'practice' },
        { id: 'i3', text: 'Set goal to use centering technique before difficult conversations', category: 'goal' },
      ],
      summary: 'Made significant progress in understanding emotional triggers and developing coping strategies for high-stress workplace situations.',
    },
    {
      id: 'session-2',
      title: 'Understanding and Expressing Emotions Constructively',
      date: 'May 10, 2025',
      duration: '45 min',
      durationMinutes: 45,
      type: 'video',
      insights: [
        { id: 'i4', text: 'Explored patterns of emotional suppression in meetings', category: 'breakthrough' },
        { id: 'i5', text: 'Created framework for assertive communication', category: 'practice' },
        { id: 'i6', text: 'Recognized the value of expressing emotions appropriately', category: 'reflection' },
      ],
      summary: 'Developed a clear framework for expressing emotions constructively in professional settings.',
    },
    {
      id: 'session-3',
      title: 'Breaking Free from Limiting Beliefs',
      date: 'May 3, 2025',
      duration: '28 min',
      durationMinutes: 28,
      type: 'text',
      insights: [
        { id: 'i7', text: 'Identified core belief: "I need to be perfect to be valued"', category: 'breakthrough' },
        { id: 'i8', text: 'Developed countering statements for moments of self-doubt', category: 'practice' },
        { id: 'i9', text: 'Plan to practice self-compassion daily', category: 'goal' },
      ],
      summary: 'Uncovered limiting beliefs about perfectionism and started developing healthier self-talk patterns.',
    },
    {
      id: 'session-4',
      title: 'Navigating Difficult Conversations',
      date: 'April 28, 2025',
      duration: '37 min',
      durationMinutes: 37,
      type: 'voice',
      insights: [
        { id: 'i10', text: 'Practiced conflict resolution scenarios', category: 'practice' },
        { id: 'i11', text: 'Created personal mantra for challenging conversations', category: 'practice' },
        { id: 'i12', text: 'Understood the importance of preparation and mindset', category: 'reflection' },
      ],
      summary: 'Developed practical strategies for approaching difficult conversations with confidence.',
    },
    {
      id: 'session-5',
      title: 'Building Authentic Relationships',
      date: 'April 20, 2025',
      duration: '41 min',
      durationMinutes: 41,
      type: 'video',
      insights: [
        { id: 'i13', text: 'Explored barriers to authentic connection', category: 'breakthrough' },
        { id: 'i14', text: 'Practiced vulnerability in safe spaces', category: 'practice' },
        { id: 'i15', text: 'Committed to being more open with close friends', category: 'goal' },
      ],
      summary: 'Made breakthrough in understanding how to build deeper, more authentic relationships.',
    },
    {
      id: 'session-6',
      title: 'Managing Stress and Overwhelm',
      date: 'April 15, 2025',
      duration: '29 min',
      durationMinutes: 29,
      type: 'text',
      insights: [
        { id: 'i16', text: 'Identified key stressors in daily routine', category: 'breakthrough' },
        { id: 'i17', text: 'Learned progressive muscle relaxation technique', category: 'practice' },
        { id: 'i18', text: 'Planned stress-reduction schedule', category: 'goal' },
      ],
      summary: 'Developed comprehensive approach to managing daily stress and preventing overwhelm.',
    },
  ];

  // Advanced filtering and sorting logic
  const filteredAndSortedSessions = useMemo(() => {
    let filtered = pastSessions;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((session) =>
        session.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.insights.some((insight) =>
          insight.text.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply type/category filter
    if (filterBy !== 'all') {
      filtered = filtered.filter((session) => session.type === filterBy);
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'duration':
          comparison = a.durationMinutes - b.durationMinutes;
          break;
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [pastSessions, searchTerm, filterBy, sortBy, sortOrder]);

  // Calculate summary statistics
  const sessionStats = useMemo(() => {
    const totalSessions = pastSessions.length;
    const totalMinutes = pastSessions.reduce((sum, session) => sum + session.durationMinutes, 0);
    const typeBreakdown = pastSessions.reduce((acc, session) => {
      acc[session.type] = (acc[session.type] || 0) + 1;
      return acc;
    }, {} as Record<SessionType, number>);

    return {
      totalSessions,
      totalMinutes,
      typeBreakdown,
    };
  }, [pastSessions]);

  // Utility functions
  const getSessionTypeIcon = (type: SessionType) => {
    switch (type) {
      case 'text':
        return <MessageCircle className="w-4 h-4 text-gray-600" />;
      case 'voice':
        return <Mic className="w-4 h-4 text-gray-600" />;
      case 'video':
        return <Video className="w-4 h-4 text-gray-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getInsightCategoryColor = (category: SessionInsight['category']) => {
    switch (category) {
      case 'breakthrough':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'practice':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'goal':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'reflection':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };



  const handleBulkAction = (action: string) => {
    console.log(`Bulk action: ${action} for sessions:`, selectedSessions);
    setSelectedSessions([]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Enhanced Header with Stats */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Past Sessions
              </h1>
              <div className="flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <span className="font-medium">{sessionStats.totalSessions}</span>
                  <span>sessions</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span className="font-medium">{Math.floor(sessionStats.totalMinutes / 60)}h {sessionStats.totalMinutes % 60}m</span>
                  <span>total</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </header>

      {/* Advanced Filters and Search */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search sessions, insights..."
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              />
            </div>

            {/* Filters and Sort */}
            <div className="flex items-center space-x-3">
              {/* Filter Dropdown */}
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="border-gray-300"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                  <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </Button>

                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                    >
                      <div className="p-2">
                        {[
                          { value: 'all', label: 'All Sessions', count: sessionStats.totalSessions },
                          { value: 'text', label: 'Text Sessions', count: sessionStats.typeBreakdown.text || 0 },
                          { value: 'voice', label: 'Voice Sessions', count: sessionStats.typeBreakdown.voice || 0 },
                          { value: 'video', label: 'Video Sessions', count: sessionStats.typeBreakdown.video || 0 },
                        ].map((filter) => (
                          <button
                            key={filter.value}
                            onClick={() => {
                              setFilterBy(filter.value as FilterOption);
                              setShowFilters(false);
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                              filterBy === filter.value
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            <span>{filter.label}</span>
                            <span className="text-xs text-gray-500">{filter.count}</span>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Sort */}
              <Button
                variant="outline"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="border-gray-300"
              >
                {sortOrder === 'desc' ? <SortDesc className="w-4 h-4" /> : <SortAsc className="w-4 h-4" />}
              </Button>

              {/* Bulk Actions */}
              {selectedSessions.length > 0 && (
                <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-300">
                  <span className="text-sm text-gray-600">{selectedSessions.length} selected</span>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('export')}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('delete')}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Session Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AnimatePresence>
            {filteredAndSortedSessions.length > 0 ? (
              filteredAndSortedSessions.map((session, index) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group"
                >
                  {/* Session Header */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="flex items-center space-x-2">
                            {getSessionTypeIcon(session.type)}
                            <span className="text-sm font-medium text-gray-600 capitalize">
                              {session.type} Session
                            </span>
                          </div>
                          <div className="flex items-center space-x-1 text-sm text-gray-500">
                            <Calendar className="w-3 h-3" />
                            <span>{session.date}</span>
                          </div>
                          <div className="flex items-center space-x-1 text-sm text-gray-500">
                            <Clock className="w-3 h-3" />
                            <span>{session.duration}</span>
                          </div>
                        </div>

                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {session.title}
                        </h3>

                        <p className="text-sm text-gray-600 leading-relaxed">
                          {session.summary}
                        </p>
                      </div>

                      <div className="relative">
                        <Button variant="ghost" size="sm" className="text-gray-500">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Session Content */}
                  <div className="p-6">
                    {/* Key Insights */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900">Key Insights</h4>
                      <div className="space-y-2">
                        {session.insights.slice(0, 3).map((insight) => (
                          <div
                            key={insight.id}
                            className={`text-xs px-3 py-2 rounded-lg border ${getInsightCategoryColor(insight.category)}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium capitalize">{insight.category}</span>
                              <CheckCircle2 className="w-3 h-3" />
                            </div>
                            <p className="mt-1 text-xs leading-relaxed">{insight.text}</p>
                          </div>
                        ))}

                        {session.insights.length > 3 && (
                          <button className="text-xs text-gray-600 hover:text-gray-800 font-medium">
                            +{session.insights.length - 3} more insights
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="col-span-full text-center py-20"
              >
                <div className="text-6xl mb-6">🔍</div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                  No sessions found
                </h3>
                <p className="text-gray-600 mb-6">
                  Try adjusting your search terms or filters
                </p>
                <Button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterBy('all');
                  }}
                  variant="outline"
                >
                  Clear Filters
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
